import React from 'react';
// template components
import Accordion from '@nmx/template/dist/componentLibrary/modules/Accordion';
import BulletedList from '@nmx/template/dist/componentLibrary/components/BulletedList';
import Col from '@nmx/template/dist/componentLibrary/foundations/Grid/Col';
import Link from '@nmx/template/dist/componentLibrary/components/Link';
import Row from '@nmx/template/dist/componentLibrary/foundations/Grid/Row';
import SectionContainer from '@nmx/template/dist/componentLibrary/components/SectionContainer';
import Typography from '@nmx/template/dist/componentLibrary/foundations/Typography';
import WidowBlock from '@nmx/template/dist/componentLibrary/components/WidowBlock';
// icons
import MinusSignFillIcon from '@nmx/template/dist/componentLibrary/foundations/Icon/icons/utility/MinusSignFill.js';
import PlusSignFillIcon from '@nmx/template/dist/componentLibrary/foundations/Icon/icons/utility/PlusSignFill.js';

const baseIdPrefix = 'estate-planning-faq';
const baseIdSuffix = 'faq';

export default () => (
  <SectionContainer
    id={baseIdPrefix} // '-section-container' will be appended dynamically
    className='nmx-no-print'
    moduleName='faq-module'
    moduleVariation='A'>
    <Row align='center'>
      <Col
        align='center'
        small={10}
        medium={8}
        large={6}>
        <Typography
          component='h2'
          nextElement='typography'>
          You&#39;ve got estate planning questions, <WidowBlock>We&#39;ve got answers.</WidowBlock>
        </Typography>
      </Col>
    </Row>
    <Row align='center'>
      <Col
        medium={10}
        large={8}>
        <Accordion
          id={baseIdPrefix}
          variant='secondary'
          toggleOpenIcon={PlusSignFillIcon}
          toggleCloseIcon={MinusSignFillIcon}
          isFAQ
          items={[
            {
              id: `${baseIdPrefix}-who-needs-${baseIdSuffix}`,
              title: <>Who needs an <WidowBlock>estate plan&#63;</WidowBlock></>,
              contentAs: 'div',
              content: (
                <>
                  <Typography disableBottomPadding>
                    You do&#8212;no matter your age. If you&#39;re young and single, you most likely will only need basic estate planning, including things like a will and powers of attorney for your medical care and finances. If you have children, you&#39;ll want to name a guardian. And if you have substantial wealth, you may want to look into trusts to help control how your assets are taxed, managed, and distributed.&nbsp;
                    <Link
                      linkUrl='/find-a-financial-advisor/'
                      id='estate-planning-faq-talk-to-advisor-link'
                      variant='primaryDeemphasize'>
                      Talk to an advisor about what will work best for you
                    </Link>.
                  </Typography>
                </>
              ),
            },
            {
              id: `${baseIdPrefix}-what-can-estate-planning-accomplish-${baseIdSuffix}`,
              title: <>What can estate <WidowBlock>planning accomplish&#63;</WidowBlock></>,
              contentAs: 'div',
              content: (
                <>
                  <Typography disableBottomPadding>
                    A lot. Estate planning makes sure that everything stays on track when you&#39;re no longer in control. You can use a will, trust, and other estate planning documents to speak for you when you&#39;re not able to speak for yourself. If you don&#39;t have a plan for your estate when you pass away, state law (typically called intestate laws) will determine where your assets will go. Estate planning can also help maintain the value of your estate by minimizing costs associated with taxes and administration. <Link
                      linkUrl='/life-and-money/the-estate-planning-process-6-steps-to-take/'
                      id='estate-planning-faq-details-on-planning-process-link'
                      variant='primaryDeemphasize'>
                      Get more details on the estate planning process
                    </Link>.
                  </Typography>
                </>
              ),
            },
            {
              id: `${baseIdPrefix}-when-is-best-time-${baseIdSuffix}`,
              title: <>When is the best time to plan <WidowBlock>my estate&#63;</WidowBlock></>,
              contentAs: 'div',
              content: (
                <>
                  <Typography disableBottomPadding>
                    There&#39;s no single correct answer, but sooner rather than later is better. You can create an estate plan as soon as you become an adult. But typically, an estate plan comes into play because of major life events like you get married, have kids, buy real estate, etc. Even if you&#39;re not there yet, it&#39;s always a good idea to plan for the unexpected.&nbsp;
                    <Link
                      linkUrl='/find-a-financial-advisor/'
                      id='estate-planning-faq-details-second-talk-to-advisor-link'
                      variant='primaryDeemphasize'>
                      Ask an advisor about the best options for you
                    </Link>.
                  </Typography>
                </>
              ),
            },
            {
              id: `${baseIdPrefix}-how-does-it-work-${baseIdSuffix}`,
              title: <>How does <WidowBlock>estate planning</WidowBlock> work with your <WidowBlock>financial plan&#63;</WidowBlock></>,
              contentAs: 'div',
              content: (
                <>
                  <Typography disableBottomPadding>
                    An estate plan is a key part of a strong financial plan. While a financial plan will help you feel at ease about your money throughout your life, an estate plan will give you (and your loved ones) peace of mind knowing that your wishes will be followed no matter what. See how an estate plan can fit into your financial plan.&nbsp;
                    <Link
                      linkUrl='/find-a-financial-advisor/'
                      id='estate-planning-faq-third-talk-to-advisor-link'
                      variant='primaryDeemphasize'>
                      Talk with an advisor today
                    </Link>.
                  </Typography>
                </>
              ),
            },
            {
              id: `${baseIdPrefix}-beyond-my-will-${baseIdSuffix}`,
              title: <>Do I need an estate planning strategy beyond <WidowBlock>my will&#63;</WidowBlock></>,
              contentAs: 'div',
              content: (
                <>
                  <Typography disableBottomPadding>
                    Yes. A strong estate plan will also have powers of attorney for your finances and medical care, an advanced directive or living will, and depending on your situation, a trust.&nbsp;
                    <Link
                      linkUrl='/life-and-money/whats-the-difference-between-estate-planning-and-wills/'
                      id='estate-planning-faq-find-out-more-link'
                      variant='primaryDeemphasize'>
                      Find out more about what goes into an estate plan
                    </Link>.
                  </Typography>
                </>
              ),
            },
            {
              id: `${baseIdPrefix}-dont-have-children-${baseIdSuffix}`,
              title: <>Do I need an estate plan if I don&#39;t <WidowBlock>have children&#63;</WidowBlock></>,
              contentAs: 'div',
              content: (
                <>
                  <Typography disableBottomPadding>
                    Having an estate plan in place is a good idea for anyone. If you choose not to have one, your estate will default to state law where they will control the decisions versus who you chose.&nbsp;
                    <Link
                      linkUrl='/find-a-financial-advisor/'
                      id='estate-planning-faq-fourth-talk-to-advisor-link'
                      variant='primaryDeemphasize'>
                      Talk to an advisor about creating a plan for your estate
                    </Link>.
                  </Typography>
                </>
              ),
            },
            {
              id: `${baseIdPrefix}-spouse-${baseIdSuffix}`,
              title: <>Should my spouse and I share an <WidowBlock>estate plan&#63;</WidowBlock></>,
              contentAs: 'div',
              content: (
                <>
                  <Typography disableBottomPadding>
                    More often than not, yes. You&#39;ve likely built a life together, made decisions together, and managed your assets jointly along the way. So it&#39;s a good idea to figure out who will get what when you&#39;re not longer here, so there are no surprises to your spouse. Ready to create your estate plan&#63;&nbsp;
                    <Link
                      linkUrl='/find-a-financial-advisor/'
                      id='estate-planning-faq-fifth-talk-to-advisor-link'
                      variant='primaryDeemphasize'>
                      Get started by talking with an advisor
                    </Link>.
                  </Typography>
                </>
              ),
            },
            {
              id: `${baseIdPrefix}-what-happens-if-i-dont-${baseIdSuffix}`,
              title: <>What happens if I don&#39;t have an <WidowBlock>estate plan&#63;</WidowBlock></>,
              contentAs: 'div',
              content: (
                <>
                  <Typography disableBottomPadding>
                    If you don&#39;t have a plan for your estate when you pass away, state law (typically called intestate laws) will determine where your assets will go. Estate planning can also help maintain the value of your estate by minimizing costs associated with taxes and administration. Ready to create your estate plan&#63;&nbsp;
                    <Link
                      linkUrl='/find-a-financial-advisor/'
                      id='estate-planning-faq-sixth-talk-to-advisor-link'
                      variant='primaryDeemphasize'>
                      Get matched with an advisor
                    </Link>.
                  </Typography>
                </>
              ),
            },
            {
              id: `${baseIdPrefix}-avoid-probate-${baseIdSuffix}`,
              title: <>Can estate planning help me <WidowBlock>avoid probate&#63;</WidowBlock></>,
              contentAs: 'div',
              content: (
                <>
                  <Typography nextElement='typography'>
                    Yes. Probate is when a court supervises the distribution of assets based on your will or a state&#39;s intestacy laws. It can create delays, add costs (filing and attorney&#39;s fees), and since records of probate court are public record, there&#39;s loss of privacy. Here are two ways an estate plan can avoid probate proceedings:
                  </Typography>
                  <BulletedList
                    bulletedListData={[
                      { bulletText: 'By making sure that you have beneficiary designations for your life insurance policy, individual retirement accounts, 401(k)s, etc.' },
                      { bulletText: 'By setting up a revocable trust that can give you flexibility and income throughout our life, then will transfer your estate to your beneficiaries when you die.' },
                    ]} />
                  <Typography disableBottomPadding>
                    <Link
                      linkUrl='/find-a-financial-advisor/'
                      id='estate-planning-faq-seventh-talk-to-advisor-link'
                      variant='primaryDeemphasize'>
                      Ask an advisor about the best options for your situation.
                    </Link>
                  </Typography>
                </>
              ),
            },
            {
              id: `${baseIdPrefix}-should-i-update-${baseIdSuffix}`,
              title: <>When should I update my <WidowBlock>estate plan&#63;</WidowBlock></>,
              contentAs: 'div',
              content: (
                <>
                  <Typography disableBottomPadding>
                    In general, you should update your estate plan every three to five years. You&#39;ll want to consider things like if your intentions have changed, that the right people are included, etc. However, there are some common milestones and life events that could require you to update your estate plan sooner, such as getting married or divorced, having kids, getting sick, or if you move to another state.&nbsp;
                    <Link
                      linkUrl='/find-a-financial-advisor/'
                      id='estate-planning-faq-eigth-talk-to-advisor-link'
                      variant='primaryDeemphasize'>
                      Talk to an advisor about getting your plan up to date
                    </Link>.
                  </Typography>
                </>
              ),
            },
            {
              id: `${baseIdPrefix}-inheritance-${baseIdSuffix}`,
              title: <>How do inheritance and estate <WidowBlock>taxes work&#63;</WidowBlock></>,
              contentAs: 'div',
              content: (
                <>
                  <Typography disableBottomPadding>
                    The estate tax is typically applied to your assets once you&#39;ve passed away. The federal estate tax will be assessed on the current fair market value of your assets at the time of your death. However, assets inherited by your spouse or heirs (limited to just over &#36;12M in 2022 for non&#45;spouses) are generally not subject to the federal estate tax. At the state level, estate or inheritance laws and tax rates vary by state.&nbsp;
                    <Link
                      linkUrl='/find-a-financial-advisor/'
                      id='estate-planning-faq-ninth-talk-to-advisor-link'
                      variant='primaryDeemphasize'>
                      Talk to an advisor about the best tax efficient strategies for your situation
                    </Link>.
                  </Typography>
                </>
              ),
            },
          ]}
        />
      </Col>
    </Row>
  </SectionContainer>
);
